import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { useGetUserQuery, useUpdateUserMutation } from "../usersApi";
import { useGetDesignationsQuery } from "../../designations/designationsApi";

// Fills a real gap: CreateUserModal never lets an admin set the RBAC
// designation (default permission set) for an employee-linked user - it's
// always created null - and there was no way at all to fix a typo'd full
// name after creation. PUT /users/{userId} already accepts fullName +
// designationId (isActive is handled separately by the Disable/Enable
// button), this modal is just the missing UI for those two fields.
const EditUserModal = ({ userId, onClose }) => {
  const show = Boolean(userId);
  const { data, isFetching } = useGetUserQuery(userId, { skip: !userId });
  const { data: designations = [] } = useGetDesignationsQuery();
  const [updateUser, { isLoading }] = useUpdateUserMutation();

  const [fullName, setFullName] = useState("");
  const [designationId, setDesignationId] = useState("");

  useEffect(() => {
    if (data?.profile) {
      setFullName(data.profile.fullName || "");
      setDesignationId(
        data.profile.designationId != null ? String(data.profile.designationId) : ""
      );
    }
  }, [data]);

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast.error("Full name is required");
      return;
    }
    try {
      await updateUser({
        userId,
        fullName: fullName.trim(),
        designationId: designationId ? Number(designationId) : null,
        isActive: data.profile.isActive,
      }).unwrap();
      toast.success("User updated");
      onClose();
    } catch (error) {
      toast.error(error?.message || "Could not update user");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isFetching && !data ? (
          <p>Loading...</p>
        ) : (
          <Form>
            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-control bg-light"
                readOnly
                value={data?.profile?.username || ""}
              />
              <small className="text-muted">Username cannot be changed.</small>
            </div>
            <div className="mb-3">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Designation</label>
              <select
                className="form-control"
                value={designationId}
                onChange={(e) => setDesignationId(e.target.value)}
              >
                <option value="">No designation</option>
                {designations.map((d) => (
                  <option key={d.designationId} value={d.designationId}>
                    {d.designationName}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Determines this user&apos;s default permission set.
              </small>
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading || !data}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserModal;
